import React, { useEffect, useMemo } from 'react'
import { generateTimeOptions, TimeOption } from '@disco/utils/time-utils'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
type Props = {
  selectedTime: string | null | undefined
  timeIncrement: number | 30
  onSelect: (time: TimeOption) => void
  timeZone: string
  date: Date
  timeInAdvance?: number
  onClick?: () => void
}

/**
 * A time picker component that displays available time slots based on timezone and date
 * @param timeIncrement - The time increment in minutes between available options (defaults to 30)
 * @param onSelect - Callback called when a time is selected
 * @param selectedTime - The currently selected time value
 * @param timeZone - The timezone to use for time calculations
 * @param timeInAdvance - Optional minimum time in advance required
 * @param date - The date for which to show time options
 * @param onClick - Optional callback called when the picker is clicked
 * @returns A time picker dropdown with available time slots
 */
const TimePicker = ({
  timeIncrement,
  onSelect,
  selectedTime,
  timeZone,
  timeInAdvance,
  date,
  onClick,
}: Props): React.ReactNode => {
  // Convert date to zoned date and manage when date is changed from calendar
  const selectedDate = new Date(date)
  const time = new Date()
  selectedDate.setHours(time.getHours(), time.getMinutes(), time.getSeconds())

  const timeOptions = useMemo(() => {
    return generateTimeOptions(
      date,
      timeIncrement,
      selectedDate,
      timeZone,
      timeInAdvance,
    )
  }, [date, selectedDate, timeZone])

  useEffect(() => {
    const firstOption = timeOptions[0]

    if (firstOption && !timeOptions.find(t => t.value === selectedTime)) {
      onSelect(firstOption)
    }
  }, [timeOptions, selectedTime])

  return (
    <div onClick={onClick}>
      <CustomDropdown
        scrollToTop
        value={selectedTime}
        options={timeOptions}
        placeholder="Select a time"
        arrowRendererIcon={'time-clock'}
        iconStrong
        onChange={onSelect}
      />
    </div>
  )
}

export default TimePicker
